'use client';

import {
	BigNum,
	BN,
	SpotMarketConfig,
	User,
	ZERO,
} from '@velocity-exchange/sdk';
import captureException from 'src/utils/captureException';
import useTargetAccountData from './useTargetAccountData';
import useUserAccountIsReady from './useUserAccountIsReady';

// Fraction of the account's margin requirement held back from a margin-bound
// withdrawal limit, so ordinary oracle drift between quoting and execution
// doesn't tip the on-chain margin check into a revert.
const MARGIN_DRIFT_BUFFER_BPS = new BN(20);
const BPS_DENOMINATOR = new BN(10_000);

const applyMarginDriftBuffer = (
	userAccount: User,
	marketIndex: number,
	limit: BN
): BN => {
	const { depositAmount } = userAccount.canBypassWithdrawLimits(marketIndex);

	// A limit at or above the deposit balance is bounded by the balance rather than
	// by margin, so withdrawing it in full needs no buffer — and holding some back
	// would strand dust in the position.
	if (limit.gte(depositAmount)) return limit;

	const marginCalculation = userAccount.getMarginCalculation('Initial', {
		strict: true,
	});
	const freeCollateral = marginCalculation.getCrossFreeCollateral();
	if (freeCollateral.lte(ZERO)) return limit;

	const buffer = marginCalculation.marginRequirement
		.mul(MARGIN_DRIFT_BUFFER_BPS)
		.div(BPS_DENOMINATOR);

	// the limit is proportional to free collateral, so scaling it by the buffered
	// ratio is equivalent to recomputing it against the reduced free collateral
	return limit
		.mul(BN.max(freeCollateral.sub(buffer), ZERO))
		.div(freeCollateral);
};

const useWithdrawableBankBalance = (
	spotMarket: SpotMarketConfig,
	userKey = '',
	reduceOnly = true
) => {
	const userAccount = useTargetAccountData(userKey)?.client;
	const bankPrecision = spotMarket.precisionExp;

	const SAFE_DEFAULT = BigNum.zero(bankPrecision);

	const userAccountIsReady = useUserAccountIsReady(userKey);

	try {
		if (!userAccountIsReady || userKey === '' || !userAccount) {
			return SAFE_DEFAULT;
		}

		const limit = userAccount.getWithdrawalLimit(
			spotMarket.marketIndex,
			reduceOnly
		);

		// borrows are sized against the liability side, so the buffer doesn't apply
		return BigNum.from(
			reduceOnly
				? applyMarginDriftBuffer(userAccount, spotMarket.marketIndex, limit)
				: limit,
			bankPrecision
		);
	} catch (e: any) {
		captureException(e);
		return SAFE_DEFAULT;
	}
};

export default useWithdrawableBankBalance;
